import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Eye, Users, Clock, Film, CheckCircle2 } from 'lucide-react';
import { API } from './shared';
import { LoadingSpinner } from './core';

const TYPE_LABELS = { fiction: 'Film', series: 'Série', documentary: 'Documentaire', jeunesse: 'Jeunesse', game: 'Jeu', movie: 'Film' };
const fmtWatch = (s) => {
  if (!s) return '—';
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

const COLS = [
  { id: 'title', label: 'Contenu' },
  { id: 'type', label: 'Type' },
  { id: 'category', label: 'Catégorie' },
  { id: 'duration', label: 'Durée', sortable: false },
  { id: 'created_at', label: 'Ajouté le' },
  { id: 'views', label: 'Vues', icon: Eye },
  { id: 'unique_profiles', label: 'Profils uniques', icon: Users },
  { id: 'watch_seconds', label: 'Temps visionné', icon: Clock },
  { id: 'completion_rate', label: 'Complétion', icon: CheckCircle2 },
];

export function ContentStatsTable({ theme }) {
  const light = theme === 'light';
  const [rows, setRows] = useState(null);
  const [type, setType] = useState('all');
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ col: 'views', dir: -1 });
  const [dropoff, setDropoff] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/analytics/contents-table`, { withCredentials: true }).then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const types = useMemo(() => rows ? [...new Set(rows.map(r => r.type || 'fiction'))] : [], [rows]);
  const cats = useMemo(() => rows ? [...new Set(rows.map(r => r.category).filter(Boolean))].sort() : [], [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    let out = rows;
    if (type !== 'all') out = out.filter(r => (r.type || 'fiction') === type);
    if (cat !== 'all') out = out.filter(r => r.category === cat || (r.categories || []).includes(cat));
    if (q.trim()) { const s = q.trim().toLowerCase(); out = out.filter(r => (r.title || '').toLowerCase().includes(s)); }
    const { col, dir } = sort;
    return [...out].sort((a, b) => {
      const va = a[col], vb = b[col];
      if (typeof va === 'string' || typeof vb === 'string') return String(va || '').localeCompare(String(vb || '')) * dir;
      return ((va || 0) - (vb || 0)) * dir;
    });
  }, [rows, type, cat, q, sort]);

  if (!rows) return <LoadingSpinner />;

  const toggleSort = (col) => setSort(s => s.col === col ? { col, dir: -s.dir } : { col, dir: -1 });
  const selCls = `text-xs rounded-lg px-3 py-2 outline-none border ${light ? 'bg-white border-gray-200 text-gray-800' : 'bg-[#1a0e2e] border-white/10 text-white'}`;
  const totV = filtered.reduce((a, r) => a + (r.views || 0), 0);
  const totW = filtered.reduce((a, r) => a + (r.watch_seconds || 0), 0);

  return (
    <div data-testid="content-stats-table">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className={`flex items-center gap-2 rounded-lg px-3 py-2 border ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`}>
          <Search size={13} className={light ? 'text-gray-400' : 'text-white/40'} />
          <input data-testid="cst-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un titre…"
            className={`text-xs bg-transparent outline-none w-44 ${light ? 'text-gray-800' : 'text-white'}`} />
        </div>
        <select data-testid="cst-type" value={type} onChange={e => setType(e.target.value)} className={selCls}>
          <option value="all">Tous les types</option>
          {types.map(t => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
        </select>
        <select data-testid="cst-cat" value={cat} onChange={e => setCat(e.target.value)} className={selCls}>
          <option value="all">Toutes les catégories</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className={`text-xs ml-auto ${light ? 'text-gray-500' : 'text-white/50'}`}>
          {filtered.length} contenu{filtered.length > 1 ? 's' : ''} · {totV.toLocaleString('fr-FR')} vues · {fmtWatch(totW)} visionnées
        </span>
      </div>
      <div className={`rounded-xl border overflow-x-auto ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/5'}`}>
        <table className="w-full text-left" data-testid="cst-table">
          <thead>
            <tr className={light ? 'border-b border-gray-100' : 'border-b border-white/5'}>
              {COLS.map(c => (
                <th key={c.id} className={`px-3 py-3 text-[10px] font-bold uppercase whitespace-nowrap ${light ? 'text-gray-400' : 'text-white/35'}`}>
                  {c.sortable === false ? (
                    <span className="inline-flex items-center gap-1">{c.icon && <c.icon size={11} />}{c.label}</span>
                  ) : (
                    <button data-testid={`cst-sort-${c.id}`} onClick={() => toggleSort(c.id)} className="inline-flex items-center gap-1 hover:text-[#E9641A] transition-colors">
                      {c.icon && <c.icon size={11} />}{c.label}
                      {sort.col === c.id ? (sort.dir === -1 ? <ArrowDown size={11} className="text-[#E9641A]" /> : <ArrowUp size={11} className="text-[#E9641A]" />) : <ArrowUpDown size={10} className="opacity-40" />}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id} data-testid={`cst-row-${i}`} className={`${light ? 'border-b border-gray-50 hover:bg-gray-50' : 'border-b border-white/3 hover:bg-white/3'} transition-colors`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2.5 min-w-[220px]">
                    {(r.thumbnail_url || r.poster_url)
                      ? <img src={r.thumbnail_url || r.poster_url} alt="" loading="lazy" className="w-16 h-9 rounded object-cover flex-none" />
                      : <span className={`w-16 h-9 rounded flex-none flex items-center justify-center ${light ? 'bg-gray-100' : 'bg-white/5'}`}><Film size={13} className={light ? 'text-gray-300' : 'text-white/20'} /></span>}
                    <div className="min-w-0 cursor-pointer" onClick={() => setDropoff(r)} title="Voir les zones d'abandon" data-testid={`cst-dropoff-${i}`}>
                      <p className={`text-xs font-bold truncate max-w-[240px] hover:text-[#E9641A] transition-colors ${light ? 'text-gray-900' : 'text-white'}`}>{r.title}</p>
                      {r.status === 'draft' && <span className="text-[9px] font-bold uppercase text-amber-500">Brouillon</span>}
                    </div>
                  </div>
                </td>
                <td className={`px-3 py-2 text-xs whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`}>{TYPE_LABELS[r.type] || r.type || '—'}</td>
                <td className={`px-3 py-2 text-xs capitalize whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`}>{r.category || '—'}</td>
                <td className={`px-3 py-2 text-xs whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`}>{r.duration || '—'}</td>
                <td className={`px-3 py-2 text-xs whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`}>{fmtDate(r.created_at)}</td>
                <td className="px-3 py-2 text-xs font-bold text-[#E9641A] whitespace-nowrap">{(r.views || 0).toLocaleString('fr-FR')}</td>
                <td className={`px-3 py-2 text-xs font-bold whitespace-nowrap ${light ? 'text-gray-800' : 'text-white'}`}>{(r.unique_profiles || 0).toLocaleString('fr-FR')}</td>
                <td className={`px-3 py-2 text-xs whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`}>{fmtWatch(r.watch_seconds)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.completion_rate == null ? (
                    <span className={`text-xs ${light ? 'text-gray-300' : 'text-white/25'}`}>—</span>
                  ) : (
                    <div className="flex items-center gap-2" title={`${r.completion_rate}% des profils l'ont regardé jusqu'au bout (≥ 90%)`}>
                      <div className={`w-14 h-1.5 rounded-full ${light ? 'bg-gray-100' : 'bg-white/10'}`}>
                        <div className={`h-full rounded-full ${r.completion_rate >= 60 ? 'bg-emerald-500' : r.completion_rate >= 30 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${r.completion_rate}%` }} />
                      </div>
                      <span className={`text-xs font-bold ${r.completion_rate >= 60 ? 'text-emerald-500' : r.completion_rate >= 30 ? 'text-amber-500' : 'text-red-400'}`}>{r.completion_rate}%</span>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className={`px-3 py-8 text-center text-xs ${light ? 'text-gray-400' : 'text-white/40'}`}>Aucun contenu ne correspond aux filtres.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className={`text-[11px] mt-2 ${light ? 'text-gray-400' : 'text-white/35'}`}>
        « Vues » = nombre total de lectures lancées (un même profil peut compter plusieurs fois). « Profils uniques » = nombre de profils différents ayant regardé le contenu. « Temps visionné » = cumul réel de visionnage (mesuré depuis la mise en place du suivi). « Complétion » = % des profils uniques ayant atteint au moins 90 % du contenu. Cliquez sur un titre pour voir ses zones d'abandon.
      </p>
      {dropoff && <DropoffModal content={dropoff} light={light} onClose={() => setDropoff(null)} />}
    </div>
  );
}

/* ─── ZONES D'ABANDON ─── */
const fmtT = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`;
};

function DropoffModal({ content, light, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/content-dropoff/${content.id}`, { withCredentials: true })
      .then(r => setD(r.data)).catch(() => setD({ bins: [], total_viewers: 0 }));
  }, [content.id]);
  const hint = light ? 'text-gray-400' : 'text-white/35';
  const main = light ? 'text-gray-900' : 'text-white';
  const maxBin = d ? Math.max(1, ...d.bins) : 1;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="dropoff-modal">
      <div className={`rounded-xl border w-full max-w-2xl p-6 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`text-lg font-bold ${main}`}>Zones d'abandon — {content.title}</h3>
          <button onClick={onClose} className={hint} data-testid="dropoff-close">✕</button>
        </div>
        <p className={`text-xs mb-5 ${hint}`}>Où les spectateurs se sont arrêtés. Chaque barre = 5 % du contenu ; plus la barre est haute, plus ils sont nombreux à avoir décroché à ce moment-là.</p>
        {!d ? <LoadingSpinner /> : d.total_viewers === 0 ? (
          <p className={`text-center text-sm py-8 ${hint}`}>Pas encore assez de données de visionnage sur ce contenu.</p>
        ) : (
          <>
            <div className="flex gap-4 mb-4 flex-wrap">
              <p className={`text-xs ${hint}`}><span className={`text-lg font-extrabold block ${main}`}>{d.total_viewers}</span>spectateurs</p>
              <p className={`text-xs ${hint}`}><span className="text-lg font-extrabold block text-emerald-400">{d.completed}</span>sont allés au bout</p>
              <p className={`text-xs ${hint}`}><span className="text-lg font-extrabold block text-[#E9641A]">{d.completion_rate}%</span>de complétion</p>
            </div>
            <div className="flex items-end gap-1 h-36" data-testid="dropoff-chart">
              {d.bins.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                  <span className={`absolute -top-5 text-[9px] font-bold opacity-0 group-hover:opacity-100 ${main}`}>{v}</span>
                  <div className={`w-full rounded-t transition-colors ${v === Math.max(...d.bins) && v > 0 ? 'bg-red-500' : 'bg-[#E9641A]/70 group-hover:bg-[#E9641A]'}`}
                    style={{ height: `${(v / maxBin) * 100}%`, minHeight: v > 0 ? '3px' : '1px' }}
                    title={`${v} abandon${v > 1 ? 's' : ''} entre ${fmtT(d.duration * i / 20)} et ${fmtT(d.duration * (i + 1) / 20)}`} />
                </div>
              ))}
            </div>
            <div className={`flex justify-between text-[9px] mt-1 ${hint}`}>
              <span>0 min</span><span>{fmtT(d.duration / 2)}</span><span>{fmtT(d.duration)}</span>
            </div>
            <p className={`text-[10px] mt-3 ${hint}`}>La barre rouge = la zone où le plus de spectateurs décrochent. Survolez une barre pour le détail.</p>
          </>
        )}
      </div>
    </div>
  );
}
